import React from 'react';
import { Search, Calendar, Filter, ChevronLeft, ChevronRight, BookOpen, X, Trash2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export const PHASE_COLORS = {
  stabilize: { bg: 'rgba(232,180,184,0.15)', text: '#e8b4b8', label: 'Stabilize' },
  understand: { bg: 'rgba(201,168,76,0.15)', text: '#C9A84C', label: 'Understand' },
  reclaim: { bg: 'rgba(138,158,143,0.15)', text: '#72826F', label: 'Reclaim' },
  renew: { bg: 'rgba(180,190,220,0.15)', text: '#b4bedc', label: 'Renew' },
  unknown: { bg: '#EAE4DC', text: '#6B6878', label: 'Reflection' }
};

export const JournalStats = ({ stats }) => (
  <div className="grid grid-cols-3 gap-3 mb-6">
    <div 
      className="p-3 rounded-xl text-center"
      style={{ backgroundColor: 'rgba(232, 180, 184, 0.1)' }}
    >
      <p className="text-2xl font-semibold" style={{ color: '#A9756B' }}>
        {stats.total_entries}
      </p>
      <p className="text-[#6B6878] text-xs">Entries</p>
    </div>
    <div 
      className="p-3 rounded-xl text-center"
      style={{ backgroundColor: 'rgba(201, 168, 76, 0.1)' }}
    >
      <p className="text-2xl font-semibold" style={{ color: '#C9A84C' }}>
        {stats.total_words?.toLocaleString() || 0}
      </p>
      <p className="text-[#6B6878] text-xs">Words</p>
    </div>
    <div 
      className="p-3 rounded-xl text-center"
      style={{ backgroundColor: 'rgba(138, 158, 143, 0.1)' }}
    >
      <p className="text-2xl font-semibold" style={{ color: '#72826F' }}>
        {stats.unique_days}
      </p>
      <p className="text-[#6B6878] text-xs">Days</p>
    </div>
  </div>
);

export const SearchBar = ({ value, onChange, onSubmit, onToggleFilters }) => (
  <form onSubmit={onSubmit} className="flex gap-2 mb-4">
    <div className="relative flex-1">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8798]" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Search entries..."
        className="pl-10 bg-[#F7F1EE] border-[#EAE4DC] text-[#1A1A2E] placeholder:text-[#A3A0AE]"
        data-testid="journal-search-input"
      />
    </div>
    <Button
      type="button"
      variant="outline"
      onClick={onToggleFilters}
      className="bg-[#F7F1EE] border-[#EAE4DC] text-[#1A1A2E] hover:bg-[#F0EBE4]"
      data-testid="journal-filter-toggle"
    >
      <Filter className="w-4 h-4" />
    </Button>
  </form>
);

export const FilterPanel = ({ 
  show, 
  selectedPhase, 
  onPhaseChange, 
  dateFrom, 
  onDateFromChange,
  dateTo,
  onDateToChange,
  onClear 
}) => {
  if (!show) return null;
  
  return (
    <div 
      className="p-4 rounded-xl mb-4"
      style={{ backgroundColor: '#F7F1EE' }}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="text-[#6B6878] text-xs mb-1 block">Phase</label>
          <select
            value={selectedPhase}
            onChange={(e) => onPhaseChange(e.target.value)}
            className="w-full p-2 rounded-lg bg-[#F7F1EE] border border-[#EAE4DC] text-[#1A1A2E] text-sm"
            data-testid="journal-phase-filter"
          >
            <option value="">All phases</option>
            <option value="stabilize">Stabilize</option>
            <option value="understand">Understand</option>
            <option value="reclaim">Reclaim</option>
            <option value="renew">Renew</option>
          </select>
        </div>
        <div>
          <label className="text-[#6B6878] text-xs mb-1 block">From</label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => onDateFromChange(e.target.value)}
            className="bg-[#F7F1EE] border-[#EAE4DC] text-[#1A1A2E]"
            data-testid="journal-date-from"
          />
        </div>
        <div>
          <label className="text-[#6B6878] text-xs mb-1 block">To</label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => onDateToChange(e.target.value)}
            className="bg-[#F7F1EE] border-[#EAE4DC] text-[#1A1A2E]"
            data-testid="journal-date-to"
          />
        </div>
      </div>
      <button
        onClick={onClear}
        className="mt-3 text-[#6B6878] text-sm hover:text-[#1A1A2E] transition-colors"
        data-testid="journal-clear-filters"
      >
        Clear filters
      </button>
    </div>
  );
};

export const JournalEntryCard = ({ entry, onClick }) => {
  const phaseKey = String(entry.phase ?? '').toLowerCase() || 'unknown';
  const colors = PHASE_COLORS[phaseKey] || PHASE_COLORS.unknown;
  const date = new Date(entry.created_at);
  const formattedDate = date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
  });

  return (
    <button
      onClick={onClick}
      className="w-full p-4 rounded-xl text-left transition-all hover:scale-[1.01]"
      style={{ backgroundColor: '#F7F1EE' }}
      data-testid={`journal-entry-${entry.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <span 
          className="px-2 py-0.5 rounded-full text-xs"
          style={{ backgroundColor: colors.bg, color: colors.text }}
        >
          {colors.label}
        </span>
        <span className="text-[#8A8798] text-xs flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {formattedDate}
        </span>
      </div>
      <p className="text-[#4A4A5A] text-sm mb-2 italic line-clamp-1">
        "{entry.prompt_text}"
      </p>
      <p className="text-[#2E2E42] text-sm line-clamp-2">
        {entry.entry_text}
      </p>
    </button>
  );
};

export const Pagination = ({ current, total, onPageChange }) => {
  if (total <= 1) return null;
  
  return (
    <div className="flex items-center justify-center gap-2 mt-6">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(current - 1)}
        disabled={current <= 1}
        className="bg-[#F7F1EE] border-[#EAE4DC] text-[#1A1A2E] hover:bg-[#F0EBE4] disabled:opacity-50"
        data-testid="journal-prev-page"
      >
        <ChevronLeft className="w-4 h-4" />
      </Button>
      <span className="text-[#4A4A5A] text-sm px-4">
        Page {current} of {total}
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onPageChange(current + 1)}
        disabled={current >= total}
        className="bg-[#F7F1EE] border-[#EAE4DC] text-[#1A1A2E] hover:bg-[#F0EBE4] disabled:opacity-50"
        data-testid="journal-next-page"
      >
        <ChevronRight className="w-4 h-4" />
      </Button>
    </div>
  );
};

export const EntryModal = ({ 
  entry, 
  onClose, 
  onDelete, 
  onExport, 
  deleting, 
  exporting 
}) => {
  if (!entry) return null;
  
  const phaseKey = String(entry.phase ?? '').toLowerCase() || 'unknown';
  const colors = PHASE_COLORS[phaseKey] || PHASE_COLORS.unknown;
  const date = new Date(entry.created_at);
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)' }}
      onClick={onClose}
    >
      <div 
        className="w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-2xl p-6"
        style={{ backgroundColor: '#FFFFFF', border: '1px solid #EAE4DC' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <span 
              className="px-2 py-0.5 rounded-full text-xs inline-block mb-2"
              style={{ backgroundColor: colors.bg, color: colors.text }}
            >
              {colors.label}
            </span>
            <p className="text-[#8A8798] text-sm">
              {date.toLocaleDateString('en-US', { 
                weekday: 'long',
                month: 'long', 
                day: 'numeric',
                year: 'numeric'
              })}
            </p>
          </div>
          <button 
            onClick={onClose}
            className="text-[#6B6878] hover:text-[#1A1A2E] transition-colors"
            data-testid="entry-modal-close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: 'rgba(201, 168, 76, 0.1)' }}>
          <p className="text-[#6B6878] text-xs mb-1">Prompt</p>
          <p className="text-[#2E2E42] italic">"{entry.prompt_text}"</p>
        </div>
        
        <div className="mb-6">
          <p className="text-[#6B6878] text-xs mb-2">Your reflection</p>
          <p className="text-[#1A1A2E] leading-relaxed whitespace-pre-wrap">
            {entry.entry_text}
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={() => onExport(entry.id)}
            disabled={exporting}
            variant="outline"
            className="flex-1 bg-[#F7F1EE] border-[#EAE4DC] text-[#1A1A2E] hover:bg-[#F0EBE4]"
            data-testid="entry-export-button"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export PDF'}
          </Button>
          <Button
            onClick={() => onDelete(entry.id)}
            disabled={deleting}
            variant="outline"
            className="bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20"
            data-testid="entry-delete-button"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const EmptyState = () => (
  <div className="text-center py-12">
    <BookOpen className="w-12 h-12 mx-auto mb-4 text-[#B4B0BC]" />
    <p className="text-[#6B6878]">No journal entries yet</p>
    <p className="text-[#A3A0AE] text-sm mt-1">
      Start your reflection journey with Sage
    </p>
  </div>
);
